<template>
  <section class="zones" :class="{ 'zones--flush': flush }">
    <div class="zones__head">
      <div v-if="!flush" class="zones__label">Zonas con más demanda · últimos {{ range }} días</div>
      <div class="zones__toggle">
        <button
          type="button"
          class="zones__tg"
          :class="{ 'zones__tg--on': metric === 'visits' }"
          @click="metric = 'visits'"
        >Visitas</button>
        <button
          type="button"
          class="zones__tg"
          :class="{ 'zones__tg--on': metric === 'inquiries' }"
          @click="metric = 'inquiries'"
        >Consultas</button>
      </div>
    </div>

    <div class="zones__grid">
      <!-- demand heat map -->
      <div class="zones__mapcard">
        <gmap-map
          ref="zmap"
          :center="center"
          :zoom="12"
          :options="mapOpts"
          class="zones__map"
        />
        <div v-if="!loading && !hasPoints" class="zones__mapempty">Sin datos geográficos en este período</div>
      </div>

      <!-- rankings -->
      <div class="zones__rankcard">
        <div class="zones__sublabel">Barrios <span class="zones__hint">por {{ metricLabel }}</span></div>
        <div v-if="neighborhoods.length" class="zones__list">
          <div v-for="(n, i) in neighborhoods" :key="'n' + i" class="zones__row">
            <span class="zones__rank">{{ i + 1 }}</span>
            <span class="zones__name">{{ n.neighborhood }}<em>{{ n.city }}</em></span>
            <span class="zones__track"><span class="zones__fill" :style="{ width: barPct(n, maxN) }"></span></span>
            <span class="zones__val">{{ metricVal(n) }}</span>
          </div>
        </div>
        <div v-else class="zones__empty">Sin barrios con datos todavía — se completan a medida que geolocalizamos las propiedades.</div>

        <div class="zones__sublabel zones__sublabel--mt">Ciudades</div>
        <div v-if="cities.length" class="zones__list">
          <div v-for="(c, i) in cities" :key="'c' + i" class="zones__row">
            <span class="zones__rank">{{ i + 1 }}</span>
            <span class="zones__name">{{ c.city }}</span>
            <span class="zones__track"><span class="zones__fill" :style="{ width: barPct(c, maxC) }"></span></span>
            <span class="zones__val">{{ metricVal(c) }}</span>
          </div>
        </div>
        <div v-else class="zones__empty">Sin ciudades con datos en este período.</div>
      </div>
    </div>
  </section>
</template>

<script>
import { gmapApi } from "vue2-google-maps";
import { AreaReport } from "@/graphql/queries/analytics";
import { localDateKey, BROWSER_TZ } from "@/utils/charts";

export default {
  props: {
    range: { type: Number, default: 30 },
    opFilter: { type: String, default: null },
    // rendered inside a ReportPanel, which supplies the card and the title
    flush: { type: Boolean, default: false }
  },

  data: () => ({
    report: null,
    loading: false,
    metric: "visits",
    center: { lat: -29.15, lng: -59.65 }, // north Santa Fe; fitBounds overrides
    mapOpts: {
      zoomControl: true,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: true,
      disableDefaultUi: true
    },
    _circles: [],
    _markers: [],
    _map: null
  }),

  created() {
    this.fetch();
  },

  mounted() {
    if (this.$refs.zmap && this.$refs.zmap.$mapPromise) {
      this.$refs.zmap.$mapPromise.then(map => {
        this._map = map;
        this.renderHeat();
      });
    }
  },

  watch: {
    range() {
      this.fetch();
    },
    opFilter() {
      this.fetch();
    },
    metric() {
      this.renderHeat();
    }
  },

  computed: {
    rangeDates() {
      const to = new Date();
      const from = new Date();
      from.setDate(to.getDate() - (this.range - 1));
      return { from: localDateKey(from), to: localDateKey(to) };
    },
    metricLabel() {
      return this.metric === "inquiries" ? "consultas" : "visitas";
    },
    neighborhoods() {
      return this.sortByMetric(this.report && this.report.topNeighborhoods);
    },
    cities() {
      return this.sortByMetric(this.report && this.report.topCities);
    },
    maxN() {
      return Math.max(1, ...this.neighborhoods.map(this.metricVal));
    },
    maxC() {
      return Math.max(1, ...this.cities.map(this.metricVal));
    },
    hasPoints() {
      return (this.report && this.report.heatPoints || []).some(p => this.metricVal(p) > 0);
    }
  },

  methods: {
    metricVal(row) {
      return (this.metric === "inquiries" ? row.inquiries : row.visits) || 0;
    },
    sortByMetric(list) {
      return [...(list || [])].sort((a, b) => this.metricVal(b) - this.metricVal(a)).slice(0, 10);
    },
    barPct(row, max) {
      return Math.round((this.metricVal(row) / max) * 100) + "%";
    },

    fetch() {
      this.loading = true;
      this.$apollo
        .query({
          query: AreaReport,
          fetchPolicy: "no-cache",
          variables: {
            from: this.rangeDates.from,
            to: this.rangeDates.to,
            timeZone: BROWSER_TZ,
            operationType: this.opFilter
          }
        })
        .then(res => {
          this.report = res.data.areaReport;
          this.renderHeat();
        })
        .catch(() => {
          this.report = null;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // Render demand as weighted translucent circles (radius + opacity scale with
    // intensity, so dense areas blend into hotspots). Native google.maps.Circle —
    // the old visualization HeatmapLayer was removed in Maps JS v3.65.
    renderHeat() {
      const g = gmapApi();
      if (!this._map || !g || !g.maps) return;

      (this._circles || []).forEach(c => c.setMap(null));
      (this._markers || []).forEach(m => m.setMap(null));
      this._circles = [];
      this._markers = [];

      const pts = (this.report && this.report.heatPoints || [])
        .map(p => ({ lat: p.lat, lng: p.lng, w: this.metricVal(p) }))
        .filter(p => p.w > 0);
      if (!pts.length) return;

      const max = Math.max(...pts.map(p => p.w));
      pts.forEach(p => {
        const t = p.w / max; // 0..1 intensity
        this._circles.push(new g.maps.Circle({
          map: this._map,
          center: { lat: p.lat, lng: p.lng },
          radius: 130 + t * 520,            // metres
          strokeWeight: 0,
          fillColor: "#b52118",
          fillOpacity: 0.12 + t * 0.33,
          clickable: false
        }));
        // always-visible marker per visited property (pixel-scaled — works when sparse)
        this._markers.push(new g.maps.Marker({
          map: this._map,
          position: { lat: p.lat, lng: p.lng },
          clickable: false,
          icon: { path: g.maps.SymbolPath.CIRCLE, scale: 4 + t * 7, fillColor: "#b52118", fillOpacity: 0.9, strokeColor: "#fff", strokeWeight: 1 }
        }));
      });

      // Pan/zoom to the most-visited area: bucket points into ~11km cells, take the
      // heaviest, and fit to its local cluster — otherwise fitting all points spans
      // far-apart towns and the heat shrinks to nothing.
      const key = p => `${Math.round(p.lat / 0.1)}|${Math.round(p.lng / 0.1)}`;
      const cells = {};
      pts.forEach(p => {
        const id = key(p);
        const k = cells[id] || (cells[id] = { w: 0, lat: 0, lng: 0 });
        k.w += p.w; k.lat += p.lat * p.w; k.lng += p.lng * p.w;
      });
      const top = Object.values(cells).sort((a, b) => b.w - a.w)[0];
      const cLat = top.lat / top.w;
      const cLng = top.lng / top.w; // weighted centroid of the heaviest cluster
      const near = pts.filter(p => Math.abs(p.lat - cLat) < 0.06 && Math.abs(p.lng - cLng) < 0.06);

      const bounds = new g.maps.LatLngBounds();
      (near.length ? near : pts).forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));

      // wait for the rankings to render (which stretches this card), then resize the
      // map to the new height and fit — otherwise it fits at the stale 360px height
      this.$nextTick(() => {
        g.maps.event.trigger(this._map, "resize");
        this._map.fitBounds(bounds);
        g.maps.event.addListenerOnce(this._map, "idle", () => {
          if (this._map.getZoom() > 15) this._map.setZoom(15);
        });
      });
    }
  },

  beforeDestroy() {
    (this._circles || []).forEach(c => c.setMap(null));
    (this._markers || []).forEach(m => m.setMap(null));
  }
};
</script>

<style scoped>
.zones { margin-top: 22px; margin-bottom: 32px; }
.zones--flush { margin: 0; padding: 16px 18px 18px; }
.zones--flush .zones__head { justify-content: flex-end; }
.zones__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.zones__label { font-size: 13px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #756f64; }
.zones__toggle { display: inline-flex; background: #f1ece3; border-radius: 9px; padding: 3px; gap: 2px; }
.zones__tg {
  border: none; background: transparent; cursor: pointer; font: inherit; font-size: 12.5px; font-weight: 600;
  color: #6a645a; border-radius: 7px; padding: 5px 12px; transition: background 0.15s, color 0.15s;
}
.zones__tg--on { background: #fff; color: #1a1814; font-weight: 700; box-shadow: 0 1px 4px -1px rgba(40, 33, 22, 0.3); }

.zones__grid { display: grid; grid-template-columns: 1.3fr 1fr; gap: 18px; }
@media (max-width: 900px) { .zones__grid { grid-template-columns: 1fr; } }

.zones__mapcard {
  position: relative; background: #fff; border: 1px solid #ece7de; border-radius: 14px; overflow: hidden; min-height: 360px;
}
/* fill the card — the grid row stretches both columns to the taller (rankings) height */
.zones__map { position: absolute; top: 0; right: 0; bottom: 0; left: 0; width: 100%; height: 100%; }
.zones__mapempty {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  color: #a39a8c; font-size: 13px; background: #faf8f4; text-align: center; padding: 0 24px;
}

.zones__rankcard { background: #fff; border: 1px solid #ece7de; border-radius: 14px; padding: 16px 18px; }
.zones__sublabel { font-size: 10px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: #a39a8c; margin-bottom: 10px; }
.zones__sublabel--mt { margin-top: 20px; }
.zones__hint { color: #c2b9aa; font-weight: 600; text-transform: none; letter-spacing: 0; }

.zones__list { display: flex; flex-direction: column; gap: 9px; }
.zones__row { display: grid; grid-template-columns: 18px 1fr 84px auto; align-items: center; gap: 10px; }
.zones__rank { font-size: 12px; font-weight: 700; color: #c2b9aa; text-align: center; }
.zones__name { font-size: 13px; font-weight: 600; color: #1a1814; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.zones__name em { font-style: normal; color: #a39a8c; font-weight: 500; font-size: 11.5px; margin-left: 6px; }
.zones__track { height: 7px; background: #f1ece3; border-radius: 4px; overflow: hidden; }
.zones__fill { display: block; height: 100%; background: #b52118; border-radius: 4px; }
.zones__val { font-size: 13px; font-weight: 700; color: #1a1814; text-align: right; min-width: 34px; }

.zones__empty { color: #a39a8c; font-size: 12.5px; line-height: 1.5; }
</style>
